import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarIcon, CheckCircle2, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quote() {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [propertyType, setPropertyType] = useState("");
  const [propertySize, setPropertySize] = useState("");
  const [date, setDate] = useState();
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { axios.get(`${API}/services`).then((r) => setServices(r.data.services || [])); }, []);

  function toggle(id) {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  }

  async function submit() {
    if (!contact.name || !contact.email || !contact.phone) {
      toast.error("Name, email and phone required."); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...contact,
        services: selected,
        property_type: propertyType,
        property_size: propertySize,
        preferred_date: date ? format(date, "yyyy-MM-dd") : "",
        source: "quote_form",
      });
      toast.success("Brief received. We'll respond within 24 hours.");
      setDone(true);
    } catch { toast.error("Transmission failed — please call (833) 8BESPOKE."); }
    finally { setSubmitting(false); }
  }

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center" data-testid="quote-success" style={{ background: "var(--ivory)" }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <CheckCircle2 size={48} style={{ color: "var(--brass)" }} className="mx-auto" />
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] mt-8" style={{ color: "var(--muted)" }}>Brief received</div>
          <h1 className="font-display text-5xl md:text-6xl mt-4" style={{ letterSpacing: "-0.03em" }}>
            Standby, <span className="font-display-italic" style={{ color: "var(--brass)" }}>operator.</span>
          </h1>
          <p className="mt-6 font-body text-lg" style={{ color: "var(--navy-soft)" }}>A member of Bespoke will contact you within 24 hours to schedule a site walkthrough.</p>
          <a href="/" className="btn-primary mt-10 inline-flex">Return Home <ArrowUpRight size={14} className="arrow" /></a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" style={{ background: "var(--ivory)" }}>
      <section className="pt-16 pb-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>Chapter Three · The Brief</div>
          <h1 className="font-display mt-4" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", lineHeight: 0.95, letterSpacing: "-0.04em" }}>
            Request a <span className="font-display-italic" style={{ color: "var(--brass)" }}>quote.</span>
          </h1>
          <p className="mt-6 font-body text-lg" style={{ color: "var(--navy-soft)" }}>Give us the target. We'll deploy the plan.</p>

          <div className="mt-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.24em] flex-wrap">
            {["Services", "Property", "Contact"].map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-6 w-6 flex items-center justify-center" style={{ background: step === i + 1 ? "var(--navy)" : "transparent", color: step === i + 1 ? "var(--ivory)" : "var(--muted)", border: "1px solid var(--line-2)" }}>{i + 1}</div>
                <span style={{ color: step === i + 1 ? "var(--navy)" : "var(--muted)" }}>{label}</span>
                {i < 2 && <span style={{ color: "var(--muted)" }}>/</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="thin-border p-8 md:p-12" style={{ background: "var(--ivory)" }}>
            {step === 1 && (
              <div data-testid="step-services">
                <h2 className="font-display text-3xl mb-6" style={{ letterSpacing: "-0.02em" }}>Which services are in scope?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map((s) => (
                    <button key={s.id} data-testid={`select-${s.id}`} onClick={() => toggle(s.id)} className="text-left p-4 transition-colors" style={{ border: `1px solid ${selected.includes(s.id) ? "var(--brass)" : "var(--line)"}`, background: selected.includes(s.id) ? "rgba(207,157,21,0.08)" : "transparent" }}>
                      <div className="font-mono text-[9px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>Select</div>
                      <div className="font-display text-xl mt-1" style={{ letterSpacing: "-0.02em" }}>{s.name}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-10 flex justify-end">
                  <button data-testid="step-1-next" onClick={() => setStep(2)} className="btn-primary" disabled={selected.length === 0}>Continue <ArrowUpRight size={14} className="arrow" /></button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-property">
                <h2 className="font-display text-3xl mb-6" style={{ letterSpacing: "-0.02em" }}>Property intel</h2>
                <div className="space-y-6">
                  <div>
                    <label className="overline block mb-2">Property Type</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {["Retail", "Office", "Industrial", "Multi-Unit"].map((t) => (
                        <button key={t} data-testid={`ptype-${t.toLowerCase()}`} onClick={() => setPropertyType(t)} className="p-3 font-mono text-xs uppercase tracking-widest transition-colors" style={{ border: `1px solid ${propertyType === t ? "var(--brass)" : "var(--line)"}`, background: propertyType === t ? "rgba(207,157,21,0.1)" : "transparent", color: propertyType === t ? "var(--brass)" : "var(--navy)" }}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="overline block mb-2">Approx. Property Size</label>
                    <input data-testid="property-size" className="field-boxed" placeholder="e.g. 12,000 sq ft or 40-space parking lot" value={propertySize} onChange={(e) => setPropertySize(e.target.value)} />
                  </div>
                  <div>
                    <label className="overline block mb-2">Preferred Assessment Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button data-testid="date-picker-btn" className="field-boxed text-left flex items-center gap-3">
                          <CalendarIcon size={16} style={{ color: "var(--brass)" }} />
                          {date ? format(date, "PPP") : <span style={{ color: "var(--muted)" }}>Pick a date</span>}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0" align="start" style={{ background: "var(--ivory)", border: "1px solid var(--line-2)", borderRadius: 0 }}>
                        <Calendar mode="single" selected={date} onSelect={setDate} className="rounded-none" />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="mt-10 flex justify-between">
                  <button data-testid="step-2-back" onClick={() => setStep(1)} className="btn-ghost">Back</button>
                  <button data-testid="step-2-next" onClick={() => setStep(3)} className="btn-primary">Continue <ArrowUpRight size={14} className="arrow" /></button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-contact">
                <h2 className="font-display text-3xl mb-6" style={{ letterSpacing: "-0.02em" }}>Point of contact</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="input-name" className="field-boxed" placeholder="Full name*" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                  <input data-testid="input-company" className="field-boxed" placeholder="Company" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
                  <input data-testid="input-email" className="field-boxed" placeholder="Email*" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                  <input data-testid="input-phone" className="field-boxed" placeholder="Phone*" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                </div>
                <textarea data-testid="input-message" className="field-boxed mt-4 min-h-[120px]" placeholder="Anything else we should know?" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
                <div className="mt-10 flex justify-between">
                  <button data-testid="step-3-back" onClick={() => setStep(2)} className="btn-ghost">Back</button>
                  <button data-testid="submit-quote" onClick={submit} disabled={submitting} className="btn-primary">{submitting ? "Transmitting…" : "Submit Brief"} <ArrowUpRight size={14} className="arrow" /></button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
